import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CALENDAR_RESPONSE_LABEL,
  CALENDAR_SERIES_SCOPE_LABEL,
  calendarResponseSchema,
  type CalendarEvent,
  type CalendarResponse,
  type CalendarSeriesScope,
} from '@avslack/shared';
import { calendarKey, deleteCalendarEvent, respondCalendarEvent } from '../data/calendar';
import { useUsers } from '../data/users';
import { Dialog } from './Dialog';

/**
 * MEGBESZELES (docs/23): valasz-gombok, a resztvevok allasa, a torles es a
 * sorozat-hatokor kerdese. A naptarban es a "Naptar" uzenetkartyajan is ezek
 * jelennek meg - ugyanaz a viselkedes mindket helyen.
 */

const RESPONSES = calendarResponseSchema.options;

const RESPONSE_STYLE: Record<CalendarResponse, string> = {
  accepted: 'border-[var(--success)] bg-[var(--success)] text-white',
  tentative: 'border-accent bg-accent-soft text-accent',
  declined: 'border-[var(--danger)] bg-[var(--danger)] text-white',
};

/** Igen / Talan / Nem - a kivalasztott kitoltve. Ujra ugyanarra nyomva nem tortenik semmi. */
export function ResponseButtons({
  eventId,
  current,
  size = 'md',
}: {
  eventId: string;
  current: CalendarResponse | null;
  size?: 'sm' | 'md';
}) {
  const queryClient = useQueryClient();
  const answer = useMutation({
    mutationFn: (response: CalendarResponse) => respondCalendarEvent(eventId, response),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: calendarKey }),
  });
  const shown = answer.isPending ? (answer.variables ?? current) : current;

  return (
    <div
      className="flex flex-wrap items-center gap-1.5"
      role="group"
      aria-label="Válasz a meghívóra"
    >
      {RESPONSES.map((response) => {
        const active = shown === response;
        return (
          <button
            key={response}
            type="button"
            aria-pressed={active}
            disabled={answer.isPending}
            onClick={() => !active && answer.mutate(response)}
            className={[
              'rounded-control border font-semibold transition-colors disabled:opacity-70',
              size === 'sm' ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-xs',
              active
                ? RESPONSE_STYLE[response]
                : 'border-border text-ink-muted hover:bg-surface-hover hover:text-ink',
            ].join(' ')}
          >
            {CALENDAR_RESPONSE_LABEL[response]}
          </button>
        );
      })}
      {answer.isError && (
        <span role="alert" className="text-xs text-danger">
          {answer.error instanceof Error ? answer.error.message : 'Nem sikerült.'}
        </span>
      )}
    </div>
  );
}

/** A resztvevok allasa: Igen 5 · Talan 1 · Nem 2 · nem valaszolt 3 - kinyithato nevsorral. */
export function AttendeeSummary({ event }: { event: CalendarEvent }) {
  const { nameOf } = useUsers();
  const [open, setOpen] = useState(false);
  const others = event.attendees.filter((a) => a.userId !== event.createdBy);
  const groups: { key: CalendarResponse | 'none'; label: string; ids: string[] }[] = [
    ...RESPONSES.map((response) => ({
      key: response,
      label: CALENDAR_RESPONSE_LABEL[response],
      ids: others.filter((a) => a.response === response).map((a) => a.userId),
    })),
    {
      key: 'none' as const,
      label: 'Nem válaszolt',
      ids: others.filter((a) => a.response === null).map((a) => a.userId),
    },
  ];

  if (others.length === 0) return null;
  return (
    <div className="text-xs">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="flex flex-wrap items-center gap-x-2 gap-y-1 text-ink-muted hover:text-ink"
      >
        {groups
          .filter((group) => group.ids.length > 0)
          .map((group) => (
            <span key={group.key}>
              <span className="font-semibold text-ink">{group.ids.length}</span>{' '}
              {group.label.toLowerCase()}
            </span>
          ))}
        <span className="underline">{open ? 'elrejtés' : 'kik?'}</span>
      </button>
      {open && (
        <ul className="mt-2 space-y-1">
          {groups
            .filter((group) => group.ids.length > 0)
            .map((group) => (
              <li key={group.key}>
                <span className="font-semibold">{group.label}:</span>{' '}
                <span className="text-ink-muted">
                  {group.ids.map((id) => nameOf(id)).join(', ')}
                </span>
              </li>
            ))}
        </ul>
      )}
    </div>
  );
}

/** Ismetlodo sorozatnal: mire vonatkozzon a modositas / torles. */
function ScopeChoice({
  value,
  onChange,
}: {
  value: CalendarSeriesScope;
  onChange: (scope: CalendarSeriesScope) => void;
}) {
  return (
    <fieldset className="space-y-1.5">
      <legend className="mb-1 text-xs font-semibold">Ismétlődő esemény</legend>
      {(['one', 'following', 'all'] as const).map((scope) => (
        <label key={scope} className="flex items-center gap-2 text-sm">
          <input
            type="radio"
            name="series-scope"
            checked={value === scope}
            onChange={() => onChange(scope)}
            className="accent-[var(--accent)]"
          />
          {CALENDAR_SERIES_SCOPE_LABEL[scope]}
        </label>
      ))}
    </fieldset>
  );
}

/** Modositas elott: melyik alkalmakat modositjuk? (Csak ismetlodo sorozatnal.) */
export function EditScopeDialog({
  title,
  onPick,
  onClose,
}: {
  title: string;
  onPick: (scope: CalendarSeriesScope) => void;
  onClose: () => void;
}) {
  const [scope, setScope] = useState<CalendarSeriesScope>('one');
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()} title={`Módosítás: ${title}`}>
      <div className="space-y-4">
        <ScopeChoice value={scope} onChange={setScope} />
        <p className="text-xs text-ink-muted">
          Mindenki üzenetet kap a változásról. Ha az időpont változik, a válaszok törlődnek, és újra
          vissza kell igazolni.
        </p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-control px-3 py-2 text-sm text-ink-muted hover:bg-surface-hover"
          >
            Mégsem
          </button>
          <button
            type="button"
            onClick={() => onPick(scope)}
            className="rounded-control bg-accent px-4 py-2 text-sm font-semibold text-accent-ink hover:bg-accent-hover"
          >
            Tovább
          </button>
        </div>
      </div>
    </Dialog>
  );
}

/**
 * TORLES - rakerdez: csak nalam, vagy mindenkinek (lemondas, csak a szervezo).
 * Ismetlodonel azt is, melyik alkalmakat.
 */
export function DeleteMeetingDialog({
  eventId,
  title,
  recurring,
  occurrenceStart,
  isOrganizer,
  onClose,
  onDone,
}: {
  eventId: string;
  title: string;
  recurring: boolean;
  occurrenceStart: string;
  isOrganizer: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [scope, setScope] = useState<CalendarSeriesScope>(recurring ? 'one' : 'all');
  const [whom, setWhom] = useState<'me' | 'everyone'>(isOrganizer ? 'everyone' : 'me');
  const remove = useMutation({
    mutationFn: () =>
      deleteCalendarEvent(eventId, { scope, occurrence: occurrenceStart, for: whom }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: calendarKey });
      onDone();
    },
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()} title={`Törlés: ${title}`}>
      <div className="space-y-4">
        {recurring && <ScopeChoice value={scope} onChange={setScope} />}
        <fieldset className="space-y-1.5">
          <legend className="mb-1 text-xs font-semibold">Kinek törlődjön?</legend>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="radio"
              name="delete-whom"
              checked={whom === 'me'}
              onChange={() => setWhom('me')}
              className="mt-1 accent-[var(--accent)]"
            />
            <span>
              Csak nálam
              <span className="block text-xs text-ink-muted">
                {isOrganizer
                  ? 'A te naptáradból tűnik el; a megbeszélés a többieknek megmarad.'
                  : 'A te naptáradból tűnik el. Az egész sorozat törlése elutasításnak számít - a szervező látja.'}
              </span>
            </span>
          </label>
          <label className={`flex items-start gap-2 text-sm ${isOrganizer ? '' : 'opacity-50'}`}>
            <input
              type="radio"
              name="delete-whom"
              checked={whom === 'everyone'}
              disabled={!isOrganizer}
              onChange={() => setWhom('everyone')}
              className="mt-1 accent-[var(--accent)]"
            />
            <span>
              Mindenkinek (lemondás)
              <span className="block text-xs text-ink-muted">
                {isOrganizer
                  ? 'Minden résztvevő üzenetet kap, hogy elmarad.'
                  : 'Csak a szervező mondhatja le mindenkinek.'}
              </span>
            </span>
          </label>
        </fieldset>
        {remove.isError && (
          <p role="alert" className="text-xs text-danger">
            {remove.error instanceof Error ? remove.error.message : 'Nem sikerült.'}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-control px-3 py-2 text-sm text-ink-muted hover:bg-surface-hover"
          >
            Mégsem
          </button>
          <button
            type="button"
            disabled={remove.isPending}
            onClick={() => remove.mutate()}
            className="rounded-control bg-[var(--danger)] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
          >
            {whom === 'everyone' ? 'Lemondás' : 'Törlés'}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
